export const normalizeId = (id) => String(id ?? '')

export const normalizeDisplayName = (name) =>
  String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\u2013/g, '-')
    .replace(/\band\b/g, '&')

export const stripTotalPrefix = (normalizedName) => normalizedName.replace(/^total\s+/, '')

export const parseAttributeLevel = (attribute) => {
  if (!attribute?.Details) {
    return null
  }
  try {
    const details =
      typeof attribute.Details === 'string' ? JSON.parse(attribute.Details) : attribute.Details
    const level = details?.level
    return level == null ? null : Number(level)
  } catch {
    return null
  }
}

export const flattenDimensionAttributes = (attributes) => {
  const flat = []
  const seen = new Set()

  const walk = (nodes) => {
    for (const node of nodes || []) {
      const id = node?.AttributeID
      if (id == null || seen.has(id)) {
        continue
      }
      seen.add(id)
      flat.push(node)
      if (node.Children?.length) {
        walk(node.Children)
      }
    }
  }

  walk(attributes)
  return flat
}

/**
 * @param {{ matchDisplayName?: (displayName: string, allowedName: string) => boolean }} [options]
 */
export function createNameMatcher(options = {}) {
  const matchDisplayName = options.matchDisplayName ?? ((displayName, allowedName) =>
    normalizeDisplayName(displayName) === normalizeDisplayName(allowedName))

  const findMatchingAllowedName = (displayName, allowedNames) => {
    for (const allowedName of allowedNames) {
      if (matchDisplayName(displayName, allowedName)) {
        return allowedName
      }
    }
    return null
  }

  const sortByAllowedNameOrder = (items, allowedNames, getDisplayName) => {
    const orderByName = new Map(
      allowedNames.map((name, index) => [normalizeDisplayName(name), index]),
    )

    return [...items].sort((left, right) => {
      const leftKey = normalizeDisplayName(getDisplayName(left))
      const rightKey = normalizeDisplayName(getDisplayName(right))
      return (orderByName.get(leftKey) ?? Number.MAX_SAFE_INTEGER)
        - (orderByName.get(rightKey) ?? Number.MAX_SAFE_INTEGER)
    })
  }

  const findAttributeByAllowedName = (flatAttributes, allowedName, allowedNames) => {
    const matches = flatAttributes.filter(
      (attribute) => findMatchingAllowedName(attribute.DisplayName, [allowedName]) != null,
    )

    if (!matches.length) {
      return null
    }

    return sortByAllowedNameOrder(matches, allowedNames, (attribute) => attribute.DisplayName)[0]
  }

  const findDashboardParentAttribute = (flatAttributes, parentName) => {
    const matches = flatAttributes.filter(
      (attribute) => findMatchingAllowedName(attribute.DisplayName, [parentName]) != null,
    )

    if (!matches.length) {
      return null
    }

    return matches.reduce((best, candidate) => {
      const bestLevel = parseAttributeLevel(best) ?? Number.MAX_SAFE_INTEGER
      const candidateLevel = parseAttributeLevel(candidate) ?? Number.MAX_SAFE_INTEGER
      return candidateLevel < bestLevel ? candidate : best
    })
  }

  return {
    findMatchingAllowedName,
    sortByAllowedNameOrder,
    findAttributeByAllowedName,
    findDashboardParentAttribute,
  }
}

export function buildGroupedHierarchy(flatAttributes, groups, config) {
  const {
    getParentName,
    getChildNames,
    allChildNames,
    findDashboardParentAttribute,
    findAttributeByAllowedName,
    findChildAttributeByAllowedName,
    dedupeChildren,
  } = config

  const groupsWithChildren = []
  const leafOnlyGroups = []

  for (const group of groups) {
    const parentAttribute = findDashboardParentAttribute(flatAttributes, getParentName(group))
    if (!parentAttribute) {
      continue
    }

    const resolveChildAttribute = findChildAttributeByAllowedName ?? findAttributeByAllowedName
    const children = getChildNames(group)
      .map((childName) => resolveChildAttribute(flatAttributes, childName, allChildNames, parentAttribute))
      .filter(Boolean)
      .map((attribute) => ({
        id: attribute.AttributeID,
        name: attribute.DisplayName,
      }))

    const uniqueChildren = dedupeChildren
      ? dedupeChildren(children, parentAttribute)
      : children

    if (uniqueChildren.length > 0) {
      groupsWithChildren.push({
        id: parentAttribute.AttributeID,
        name: parentAttribute.DisplayName,
        children: uniqueChildren,
      })
    } else {
      leafOnlyGroups.push({
        id: parentAttribute.AttributeID,
        name: parentAttribute.DisplayName,
      })
    }
  }

  return { groupsWithChildren, leafOnlyGroups }
}

export function parseGroupedSelectionToUiState(attributeIds, hierarchy, config) {
  const {
    groupsWithChildren,
    leafOnlyGroups,
    expandParentToAllChildren = false,
  } = config

  const parentIds = new Set()
  const childIds = new Set()
  const idSet = new Set((attributeIds ?? []).map(normalizeId))

  for (const group of groupsWithChildren) {
    const parentKey = normalizeId(group.id)

    if (idSet.has(parentKey)) {
      parentIds.add(group.id)
      if (expandParentToAllChildren) {
        for (const child of group.children) {
          childIds.add(child.id)
        }
      } else {
        for (const child of group.children) {
          if (idSet.has(normalizeId(child.id))) {
            childIds.add(child.id)
          }
        }
      }
      continue
    }

    for (const child of group.children) {
      if (idSet.has(normalizeId(child.id))) {
        childIds.add(child.id)
        parentIds.add(group.id)
      }
    }
  }

  for (const node of leafOnlyGroups) {
    if (idSet.has(normalizeId(node.id))) {
      parentIds.add(node.id)
    }
  }

  return {
    selectedParentIds: [...parentIds],
    selectedChildIds: [...childIds],
  }
}

export function resolveGroupedAttributeIds(selectedParentIds, selectedChildIds, hierarchy, config) {
  const {
    groupsWithChildren,
    leafOnlyGroups,
    isTotalSelection,
    useParentIdForTotal = false,
  } = config

  const selectedParents = new Set((selectedParentIds ?? []).map(normalizeId))
  const selectedChildren = new Set((selectedChildIds ?? []).map(normalizeId))
  const attributeIds = []

  for (const group of groupsWithChildren) {
    if (!selectedParents.has(normalizeId(group.id))) {
      continue
    }

    if (useParentIdForTotal && isTotalSelection(group, selectedChildIds)) {
      attributeIds.push(group.id)
      continue
    }

    const selectedItems = group.children.filter((child) =>
      selectedChildren.has(normalizeId(child.id)),
    )

    if (selectedItems.length > 0) {
      attributeIds.push(...selectedItems.map((child) => child.id))
    } else {
      attributeIds.push(group.id)
    }
  }

  for (const node of leafOnlyGroups) {
    if (selectedParents.has(normalizeId(node.id))) {
      attributeIds.push(node.id)
    }
  }

  return [...new Set(attributeIds)]
}

export function stripParentIdsWhenChildrenPresent(apiAttributeIds, groupsWithChildren) {
  const idSet = new Set((apiAttributeIds ?? []).map(normalizeId))
  const parentIdsToStrip = new Set()

  for (const group of groupsWithChildren) {
    const parentKey = normalizeId(group.id)
    if (!idSet.has(parentKey)) {
      continue
    }
    const hasSelectedChild = group.children.some((child) => idSet.has(normalizeId(child.id)))
    if (hasSelectedChild) {
      parentIdsToStrip.add(parentKey)
    }
  }

  return (apiAttributeIds ?? []).filter((id) => !parentIdsToStrip.has(normalizeId(id)))
}

/**
 * Drops parent ids for groups-with-children when none of their children remain selected.
 * Leaf-only parents (not in groupsWithChildren) are kept as-is.
 */
export function removeParentsWithNoSelectedChildren(
  selectedParentIds,
  selectedChildIds,
  groupsWithChildren,
) {
  const childIdSet = new Set((selectedChildIds ?? []).map(normalizeId))
  const groupsByParentId = new Map(
    (groupsWithChildren ?? []).map((group) => [normalizeId(group.id), group]),
  )

  return (selectedParentIds ?? []).filter((parentId) => {
    const group = groupsByParentId.get(normalizeId(parentId))
    if (!group?.children?.length) {
      return true
    }

    return group.children.some((child) => childIdSet.has(normalizeId(child.id)))
  })
}
